import { useState } from 'react';
import { validateSubdomain, type CreateProjectRequest } from '@mockd/shared';
import { getEndpointBaseUrl, getMockApiSubdomainUrl } from '../../config';
import { Alert } from '../common/Alert';

interface ProjectFormProps {
  onSubmit: (data: CreateProjectRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
}

export function ProjectForm({ onSubmit, onCancel, isLoading }: ProjectFormProps) {
  const [name, setName] = useState('');
  const [subdomain, setSubdomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Get the endpoint base URL for display
  const endpointBase = getEndpointBaseUrl();
  const isDev = import.meta.env.DEV;
  const endpointDisplay = isDev ? endpointBase.replace(/^https?:\/\//, '') + '/m/' : '';

  const handleNameChange = (value: string) => {
    setName(value);
    // Auto-generate subdomain from name if subdomain hasn't been manually edited
    if (!subdomain || subdomain === slugify(name)) {
      setSubdomain(slugify(value));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    if (!subdomain.trim()) {
      setError('Subdomain is required');
      return;
    }

    // Same rule the server enforces, so a subdomain it would reject never leaves the form
    const subdomainError = validateSubdomain(subdomain.trim());
    if (subdomainError) {
      setError(subdomainError);
      return;
    }

    try {
      await onSubmit({ name: name.trim(), subdomain: subdomain.trim() });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create project');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <Alert variant="error">{error}</Alert>
      )}

      <div className="fieldset">
        <label htmlFor="name" className="fieldset-legend">
          Project Name
        </label>
        <input
          type="text"
          id="name"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          placeholder="My API Project"
          className="input w-full"
          disabled={isLoading}
        />
      </div>

      <div className="fieldset">
        <label htmlFor="subdomain" className="fieldset-legend">
          Subdomain
        </label>
        <div className="join w-full">
          {isDev ? (
            <>
              <span className="join-item px-3 py-2 bg-base-200 border border-base-300 text-base-content/80 text-sm truncate max-w-[200px] flex items-center">
                {endpointDisplay}
              </span>
              <input
                type="text"
                id="subdomain"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="my-api"
                className="input join-item flex-1"
                aria-describedby="subdomain-hint"
                disabled={isLoading}
              />
            </>
          ) : (
            <>
              <input
                type="text"
                id="subdomain"
                value={subdomain}
                onChange={(e) => setSubdomain(e.target.value.toLowerCase())}
                placeholder="my-api"
                className="input join-item flex-1"
                aria-describedby="subdomain-hint"
                disabled={isLoading}
              />
              <span className="join-item px-3 py-2 bg-base-200 border border-base-300 text-base-content/80 text-sm flex items-center">
                .mockd.sh
              </span>
            </>
          )}
        </div>
        <p id="subdomain-hint" className="label whitespace-normal text-base-content/80">
          3–63 characters: lowercase letters, numbers, and hyphens. Can't start or end with a hyphen,
          or be a reserved name like api or www.
        </p>
        <p className="label whitespace-normal text-base-content/80">
          Your mock endpoint URL will be: {getMockApiSubdomainUrl(subdomain || 'your-subdomain')}
        </p>
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isLoading}
        >
          {isLoading ? 'Creating...' : 'Create Project'}
        </button>
      </div>
    </form>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    // The cut can land on a hyphen, which the subdomain rule rejects
    .replace(/-+$/g, '');
}
